import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BankTracker = () => {
  const [shooters, setShooters] = useState([]);
  const [buyInAmount, setBuyInAmount] = useState(0);
  const [startingBankroll, setStartingBankroll] = useState('');
  const [endingBankroll, setEndingBankroll] = useState('');
  const [savedSessions, setSavedSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [userRole, setUserRole] = useState('user');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const startingBankrollRef = useRef(null);
  const endingBankrollRef = useRef(null);



  const formatCurrency = (value) => '$' + Math.abs(value).toFixed(0);

  const formatPercent = (value) => value.toFixed(2) + '%';

  const addShooter = () => {
    const starting = parseFloat(startingBankroll) || 0;
    const ending = parseFloat(endingBankroll) || 0;

    if (starting === 0) {
      alert('Please enter a starting bankroll');
      return;
    }

    if (shooters.length === 0) {
      setBuyInAmount(starting);
    }

    const winLoss = ending - starting;
    const rawPercent = starting !== 0 ? (winLoss / starting) * 100 : 0;
    // Ensure exact 2 decimal places
    const winLossPercent = Math.round(rawPercent * 100) / 100;
    const shooterNum = shooters.length + 1;

    // Calculate Player Score: rounded win/loss % + (shooter position * 10), max 10 shooters
    // For negative numbers, round away from zero (Math.floor for negatives)
    // For positive numbers, round away from zero (Math.ceil)
    const roundedPercent = winLossPercent >= 0 ?
      Math.ceil(winLossPercent) :
      Math.floor(winLossPercent);
    const playerScore = roundedPercent + Math.min(shooterNum, 10) * 10;

    const newShooter = {
      shooterNum: shooterNum,
      starting: starting,
      ending: ending,
      winLoss: winLoss,
      winLossPercent: winLossPercent,
      playerScore: playerScore
    };

    const newShooters = [...shooters, newShooter];
    setShooters(newShooters);

    setStartingBankroll(ending.toString());
    setEndingBankroll('');

    // Check if we've reached 10 shooters after adding this one
    if (newShooters.length >= 10) {
      if (window.confirm('You have completed 10 shooters. Would you like to save this session?')) {
        saveSession();
      }
    } else {
      // Focus ending bankroll input for next entry
      setTimeout(() => {
        if (endingBankrollRef.current) {
          endingBankrollRef.current.focus();
        }
      }, 100);
    }
  };

  const undoLast = () => {
    if (shooters.length === 0) {
      alert('No shooters to undo');
      return;
    }

    const newShooters = [...shooters];
    newShooters.pop();
    setShooters(newShooters);

    if (newShooters.length > 0) {
      setStartingBankroll(newShooters[newShooters.length - 1].ending.toString());
    } else {
      setStartingBankroll('');
      setBuyInAmount(0);
    }
  };

  const clearAll = () => {
    if (window.confirm('Are you sure you want to clear all shooters?')) {
      setShooters([]);
      setBuyInAmount(0);
      setStartingBankroll('');
      setEndingBankroll('');
    }
  };

  const calculateTotals = () => {
    if (shooters.length === 0) {
      return {
        endingBankroll: 0,
        totalWL: 0,
        totalWLPercent: 0,
        totalScore: 0
      };
    }

    const lastShooter = shooters[shooters.length - 1];
    const endingBankroll = lastShooter.ending;
    const totalWL = endingBankroll - buyInAmount;
    const totalWLPercent = buyInAmount !== 0 ? (totalWL / buyInAmount) * 100 : 0;
    const totalScore = shooters.reduce((sum, s) => sum + (s.playerScore || 0), 0) + totalWL;

    return {
      endingBankroll,
      totalWL,
      totalWLPercent,
      totalScore
    };
  };

  const totals = calculateTotals();

  const saveSession = async () => {
    if (shooters.length === 0) {
      alert('No data to save');
      return;
    }
    try {
      const sessionData = {
        shooters,
        buyInAmount,
        totals: calculateTotals(),
        userId: selectedUserId || undefined
      };
      if (currentSessionId) {
        await axios.put(`/api/sessions/bank/${currentSessionId}`, sessionData);
        alert('Session updated successfully');
      } else {
        const response = await axios.post(`/api/sessions/bank`, sessionData);
        setCurrentSessionId(response.data._id);
        alert('Session saved successfully');
      }
      loadSessions();
    } catch (error) {
      console.error('Error saving session:', error);
      alert('Error saving session');
    }
  };

  const loadSessions = async (userId = null) => {
    try {
      const targetUserId = userId || selectedUserId;
      console.log('Loading sessions for userId:', targetUserId);
      const url = targetUserId ? `/api/sessions/bank?userId=${targetUserId}` : `/api/sessions/bank`;
      console.log('API URL:', url);
      const response = await axios.get(url);
      setSavedSessions([]);
      setSavedSessions(response.data || []);
      console.log('Loaded sessions:', response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
      setSavedSessions([]);
    }
  };

  const loadUsers = async () => {
    let currentUserId = null;
    try {
      // Get current user info to determine role
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        setIsLoggedIn(true);
        const userData = JSON.parse(storedUserData);
        currentUserId = userData.id;
        setUserRole(userData.role || 'user');
        setSelectedUserId(userData.id); // Default to current user

        // Load users if admin/manager
        if (userData.role === 'admin' || userData.role === 'manager') {
          const response = await axios.get(`/api/admin/users`);
          setUsers(response.data);
        }
      } else {
        setIsLoggedIn(false);
        setUserRole('user');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setIsLoggedIn(false);
    }
    // Load sessions after user setup is complete
    if (currentUserId) {
      loadSessions(currentUserId);
    }
  };

  const loadSession = (session) => {
    // Map shooters to ensure they have playerScore field (handle legacy data with score/diceScore fields)
    const mappedShooters = session.shooters.map(shooter => ({
      ...shooter,
      playerScore: shooter.playerScore || shooter.diceScore || shooter.score || 0
    }));
    setShooters(mappedShooters);
    setBuyInAmount(session.buyInAmount);
    setCurrentSessionId(session._id);
    if (mappedShooters.length > 0) {
      setStartingBankroll(mappedShooters[mappedShooters.length - 1].ending.toString());
    } else {
      setStartingBankroll('');
    }
    setEndingBankroll('');
    // Focus ending bankroll input after loading session (starting is already set)
    setTimeout(() => {
      if (endingBankrollRef.current) {
        endingBankrollRef.current.focus();
      }
    }, 100);
  };

  const newSession = () => {
    setShooters([]);
    setBuyInAmount(0);
    setStartingBankroll('');
    setEndingBankroll('');
    setCurrentSessionId(null);
    // Focus starting bankroll input for new session
    setTimeout(() => {
      if (startingBankrollRef.current) {
        startingBankrollRef.current.focus();
      }
    }, 100);
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  return (
    <div style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", background: "#031A54", minHeight: "100vh", padding: "20px" }}>
      <div style={{ maxWidth: "1400px", margin: "0 auto", background: "white", borderRadius: "12px", boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)" }}>


        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "15px", padding: "20px", background: "#f5f5f5", borderBottom: "3px solid #9584AC" }}>
          <div style={{ background: "white", padding: "15px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", textAlign: "center" }}>
            <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: "8px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Buy-In Amount</div>
            <div style={{ fontSize: "1.8rem", fontWeight: "bold", color: "#1e3c72" }}>{formatCurrency(buyInAmount)}</div>
          </div>
          <div style={{ background: "white", padding: "15px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", textAlign: "center" }}>
            <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: "8px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Ending Bankroll</div>
            <div style={{ fontSize: "1.8rem", fontWeight: "bold", color: "#1e3c72" }}>{formatCurrency(totals.endingBankroll)}</div>
          </div>
          <div style={{ background: "white", padding: "15px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", textAlign: "center" }}>
            <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: "8px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total W/L $</div>
            <div style={{ fontSize: "1.8rem", fontWeight: "bold", color: totals.totalWL >= 0 ? "#2e7d32" : "#d32f2f" }}>
              {(totals.totalWL >= 0 ? "+" : "-") + formatCurrency(totals.totalWL)}
            </div>
          </div>
          <div style={{ background: "white", padding: "15px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", textAlign: "center" }}>
            <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: "8px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total W/L %</div>
            <div style={{ fontSize: "1.8rem", fontWeight: "bold", color: totals.totalWL >= 0 ? "#2e7d32" : "#d32f2f" }}>
              {(totals.totalWL >= 0 ? "+" : "") + formatPercent(totals.totalWLPercent)}
            </div>
          </div>
           <div style={{ background: "white", padding: "15px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", textAlign: "center" }}>
             <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: "8px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Player Score</div>
             <div style={{ fontSize: "1.8rem", fontWeight: "bold", background: "#2e7d32", color: "white", padding: "10px 15px", borderRadius: "12px" }}>{totals.totalScore}</div>
           </div>
        </div>

        <div style={{ padding: "20px", background: "white", borderBottom: "2px solid #e0e0e0" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px", marginBottom: "20px" }}>
            <div>
              <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "600", color: "#333", marginBottom: "5px" }}>Starting Bankroll ($)</label>
              <input
                ref={startingBankrollRef}
                type="number"
                value={startingBankroll}
                onChange={(e) => setStartingBankroll(e.target.value)}
                placeholder="100"
                step="1"
                style={{ padding: "12px", fontSize: "1rem", border: "2px solid #e0e0e0", borderRadius: "12px", width: "100%", transition: "border-color 0.2s" }}
                onFocus={(e) => e.target.style.borderColor = "#2e7d32"}
                onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "600", color: "#333", marginBottom: "5px" }}>Ending Bankroll ($)</label>
              <input
                ref={endingBankrollRef}
                type="number"
                value={endingBankroll}
                onChange={(e) => setEndingBankroll(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addShooter();
                  }
                }}
                placeholder="90"
                step="1"
                style={{ padding: "12px", fontSize: "1rem", border: "2px solid #e0e0e0", borderRadius: "12px", width: "100%", transition: "border-color 0.2s" }}
                onFocus={(e) => e.target.style.borderColor = "#2e7d32"}
                onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button onClick={addShooter} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#2e7d32", color: "white", transition: "all 0.2s" }}>➕ Add Shooter</button>
            <button onClick={undoLast} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#ffa726", color: "white", transition: "all 0.2s" }}>↶ Undo Last</button>
            <button onClick={clearAll} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#d32f2f", color: "white", transition: "all 0.2s" }}>✖ Clear All</button>
          </div>
          {isLoggedIn ? (
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px" }}>
              {(userRole === 'admin' || userRole === 'manager') && (
                <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  <label style={{ fontSize: "0.8rem", fontWeight: "600", color: "#333" }}>
                    User ({userRole}):
                  </label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => {
                      setSelectedUserId(e.target.value);
                      loadSessions(e.target.value);
                    }}
                    style={{ padding: "8px 12px", fontSize: "0.9rem", border: "2px solid #e0e0e0", borderRadius: "8px", minWidth: "200px" }}
                  >
                    {users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName} ({user.nickname})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button onClick={saveSession} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#1976d2", color: "white", transition: "all 0.2s" }}>💾 Save Session</button>
              <button onClick={newSession} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#757575", color: "white", transition: "all 0.2s" }}>🆕 New Session</button>
              <select key={savedSessions.length} onChange={(e) => {
                if (e.target.value) {
                  const session = savedSessions.find(s => s._id === e.target.value);
                  if (session) loadSession(session);
                }
                e.target.value = '';
              }} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#f5f5f5" }}>
                <option value="">📂 Load Session</option>
                {savedSessions.map(session => (
                  <option key={session._id} value={session._id}>
                    {new Date(session.createdAt).toLocaleString()} - ${session.totals.totalWL >= 0 ? "+" : ""}{session.totals.totalWL.toFixed(0)}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div style={{ marginTop: "10px", textAlign: "center", color: "#666", fontStyle: "italic" }}>
              <p>🔒 Login required to save and load sessions</p>
            </div>
          )}
        </div>

        <div style={{ padding: "20px", overflowX: "auto", maxHeight: "500px", overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: "white" }}>
            <thead>
              <tr>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.85rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>SHOOTER #</th>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.85rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>STARTING BANKROLL</th>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.85rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>ENDING BANKROLL</th>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.85rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>WIN/LOSS $</th>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.85rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>WIN/LOSS %</th>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.85rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>PLAYER SCORE</th>
              </tr>
            </thead>
            <tbody>
              {shooters.map((shooter, index) => (
                <tr key={index} style={{ cursor: "pointer" }}>
                  <td style={{ padding: "10px", textAlign: "center", background: "#f5f5f5", fontWeight: "bold", color: "#666" }}>
                    {shooter.shooterNum} of {shooters.length}
                  </td>
                  <td style={{ padding: "10px", textAlign: "center", fontWeight: "600" }}>{formatCurrency(shooter.starting)}</td>
                  <td style={{ padding: "10px", textAlign: "center", fontWeight: "600" }}>{formatCurrency(shooter.ending)}</td>
                  <td style={{ padding: "10px", textAlign: "center", fontWeight: "600", color: shooter.winLoss >= 0 ? "#2e7d32" : "#d32f2f" }}>
                    {(shooter.winLoss >= 0 ? "+" : "-") + formatCurrency(shooter.winLoss)}
                  </td>
                  <td style={{ padding: "10px", textAlign: "center", color: shooter.winLoss >= 0 ? "#2e7d32" : "#d32f2f" }}>
                    {(shooter.winLoss >= 0 ? "+" : "") + formatPercent(shooter.winLossPercent)}
                  </td>
                   <td style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#1e3c72" }}>{shooter.playerScore}</td>
                </tr>
              ))}
              {shooters.length > 0 && (
                <tr style={{ background: "#e8f5e9", fontWeight: "bold" }}>
                  <td style={{ padding: "15px 10px", textAlign: "center", borderTop: "3px solid #2e7d32" }}>Session Recap</td>
                  <td style={{ padding: "15px 10px", textAlign: "center", borderTop: "3px solid #2e7d32" }}>{formatCurrency(buyInAmount)}</td>
                  <td style={{ padding: "15px 10px", textAlign: "center", borderTop: "3px solid #2e7d32" }}>{formatCurrency(totals.endingBankroll)}</td>
                  <td style={{ padding: "15px 10px", textAlign: "center", color: totals.totalWL >= 0 ? "#2e7d32" : "#d32f2f", borderTop: "3px solid #2e7d32" }}>
                    {(totals.totalWL >= 0 ? "+" : "-") + formatCurrency(totals.totalWL)}
                  </td>
                  <td style={{ padding: "15px 10px", textAlign: "center", color: totals.totalWL >= 0 ? "#2e7d32" : "#d32f2f", borderTop: "3px solid #2e7d32" }}>
                    {(totals.totalWL >= 0 ? "+" : "") + formatPercent(totals.totalWLPercent)}
                  </td>
                   <td style={{ padding: "15px 10px", textAlign: "center", borderTop: "3px solid #2e7d32", fontWeight: "bold", color: "#1e3c72" }}>{totals.totalScore}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ padding: "15px", textAlign: "center", background: "#f5f5f5", color: "#666", fontSize: "0.85rem" }}>
          <p>Craps Players League ©2025</p>
        </div>
      </div>
    </div>
  );
};

export default BankTracker;